/**
 * Places newly created docs in the local folder cache before they become
 * visible. Cloud placements persist through the doc's initial metadata so the
 * first server commit can materialize folder link, permissions and notification
 * atomically. The mutation outbox is only a fallback while the root cache is
 * still cold.
 *
 * Only active for cloud workspaces (local workspaces have no folder RBAC).
 */
import { DebugLogger } from '@affine/debug';
import { Service } from '@toeverything/infra';

import type { DocCreateMiddleware, DocRecord } from '../../doc';
import type { DocCreateOptions } from '../../doc/types';
import type { AccessibleFolderCacheStore } from '../../organize';
import type { WorkspaceService } from '../../workspace';
import type { FolderMutationOutboxService } from '../services/folder-mutation-outbox';

const logger = new DebugLogger('FolderAssignmentMiddleware');
export const TARGET_FOLDER_UNAVAILABLE_FOR_DOC_CREATE_ERROR =
  'Target folder is no longer available for document creation';

export class FolderAssignmentDocCreateMiddleware
  extends Service
  implements DocCreateMiddleware
{
  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly folderMutationOutbox: FolderMutationOutboxService,
    private readonly accessibleFolderCache: AccessibleFolderCacheStore
  ) {
    super();
  }

  private isCloudWorkspace(): boolean {
    const workspace = this.workspaceService.workspace;
    return !!workspace && workspace.flavour !== 'local';
  }

  private isKnownUnavailableTargetFolder(folderId: string): boolean {
    const cached = this.accessibleFolderCache.getCachedFolder(folderId);
    if (cached) {
      return cached.systemKind === 'private_mail';
    }

    const optimisticallyTrashed =
      this.accessibleFolderCache.getOptimisticallyTrashedFolders();
    for (const record of optimisticallyTrashed) {
      if (
        record.id === folderId ||
        record.folders.some(folder => folder.id === folderId)
      ) {
        return true;
      }
    }

    return this.accessibleFolderCache.hasCompletedServerFetch();
  }

  beforeCreate(options: DocCreateOptions): DocCreateOptions {
    if (
      this.isCloudWorkspace() &&
      options.targetFolderId &&
      this.isKnownUnavailableTargetFolder(options.targetFolderId)
    ) {
      logger.warn('Rejected doc create for unavailable target folder', {
        targetFolderId: options.targetFolderId,
      });
      throw new Error(TARGET_FOLDER_UNAVAILABLE_FOR_DOC_CREATE_ERROR);
    }

    if (
      this.isCloudWorkspace() &&
      options.createInWorkspaceRoot === true &&
      !options.targetFolderId
    ) {
      const rootFolderId =
        this.accessibleFolderCache.getCachedWorkspaceRootFolderId();
      if (rootFolderId) {
        return { ...options, targetFolderId: rootFolderId };
      }
    }

    return options;
  }

  beforeLocalVisibility(docId: string, options: DocCreateOptions): void {
    if (!this.isCloudWorkspace()) {
      return;
    }

    if (options.createInWorkspaceRoot === true) {
      this.accessibleFolderCache.optimisticAddToRoot(docId);
      return;
    }

    if (options.targetFolderId) {
      this.accessibleFolderCache.optimisticAddDocToFolder(
        options.targetFolderId,
        docId
      );
    }
  }

  afterCreate(_doc: DocRecord, options: DocCreateOptions): void {
    if (!this.isCloudWorkspace()) {
      return;
    }

    if (options.targetFolderId) {
      // The initial Y.Doc metadata remains the fast atomic placement path.
      // Also queue the idempotent explicit placement so a document created
      // immediately inside an optimistically-created folder cannot fall back
      // to the workspace root before the parent folder materializes.
      this.folderMutationOutbox
        .enqueueAddDocToFolder(options.targetFolderId, _doc.id)
        .then(() => {
          logger.info('Queued target-folder doc assignment', {
            docId: _doc.id,
            targetFolderId: options.targetFolderId,
          });
        })
        .catch(err => {
          logger.error('Failed to queue target-folder doc assignment', {
            docId: _doc.id,
            targetFolderId: options.targetFolderId,
            err,
          });
        });
      return;
    }

    if (options.createInWorkspaceRoot === true) {
      this.folderMutationOutbox
        .enqueueAddDocToWorkspaceRoot(_doc.id)
        .then(() => {
          logger.info('Queued doc root-folder assignment', {
            docId: _doc.id,
          });
        })
        .catch(err => {
          logger.error('Failed to queue doc root-folder assignment', {
            docId: _doc.id,
            err,
          });
        });
      return;
    }
  }

  onCreateRejected(
    docId: string,
    options: DocCreateOptions,
    _error?: Error
  ): void {
    if (!this.isCloudWorkspace() || !options.targetFolderId) {
      return;
    }

    this.accessibleFolderCache.optimisticRemoveDocEverywhere(docId);
  }
}
